import {
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Checkbox,
  IconButton,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  TextField as MuiTextField,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import Grid from '@mui/material/Grid2';
import { useDialogContext } from '../../contexts/DialogContext';
import { useFormContext } from '../../contexts/FormContext';
import { useFormData } from '../../hooks/useFormValues';
import { useAppContext } from '../../contexts/AppContext';
import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Server } from '../../services/Server';
import CustomDialog from '../common/Dialog';
import ChartHelpTooltip from '../common/ChartHelpTooltip';
import TextField from './TextField';
import NumberField from './NumberField';
import SelectField from './SelectField';
import CheckBoxField from './CheckboxField';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import { isValidNumberInput } from '../../utils/utils';
import type { FieldOption } from '../form-engine/types';

type NumericValue = number | string;

interface CleaningPackagePart {
  name?: string;
  cost?: NumericValue;
  running_lifetime?: NumericValue;
  calendar_lifetime?: NumericValue;
}

interface CleaningPackageDevice {
  name?: string;
  cost?: NumericValue;
  running_lifetime?: NumericValue;
  calendar_lifetime?: NumericValue;
  yearly_cost?: NumericValue;
  parts?: CleaningPackagePart[];
}

interface CleaningPackageUtility {
  tank_volume?: NumericValue;
  consumption?: NumericValue;
  refill_duration?: NumericValue;
}

interface CleaningPackage {
  name?: string;
  type?: string;
  imageName?: string;
  description?: string;
  note?: string;
  efficiency?: NumericValue;
  linear_speed?: NumericValue;
  days_to_clean?: NumericValue;
  device?: CleaningPackageDevice | null;
  water?: CleaningPackageUtility;
  fuel?: CleaningPackageUtility;
  [key: string]: unknown;
}

interface PackageOption {
  name: string;
  value: string;
  label: string;
  data: CleaningPackage;
}

interface EditField {
  type: string;
  name?: string;
  label?: string;
  font_size?: 'h5' | 'h6';
  size?: number;
  help?: string;
  isHidden?: string;
  revertHidden?: boolean;
  fields?: EditField[];
  [key: string]: unknown;
}

interface SpecialMultiSelectField {
  name?: string;
  size?: number;
  options?: string | FieldOption[];
  [key: string]: unknown;
}

const hasOwn = (obj: object | null | undefined, key: string) => !!obj && Object.prototype.hasOwnProperty.call(obj, key);

const getTypeLabel = (type: string | undefined) => {
  if (!type) return '';
  switch (type.toLowerCase()) {
    case 'autonomous':
    case 'autonomous-robot':
      return 'Autonomous Robot';
    case 'semiautonomous-robot':
    case 'semi-autonomous':
      return 'Semi-Autonomous Robot';
    case 'tractor':
      return 'Tractor';
    case 'human':
      return 'Manual';
    case 'outsourced':
      return 'Outsourced';
    default:
      return type.charAt(0).toUpperCase() + type.slice(1);
  }
};

const SpecialMultiSelect = ({ field }: { field: SpecialMultiSelectField }) => {
  const { getFormDataItem, handleChange, setFormData } = useFormContext();
  const formData = useFormData();
  const { openDialog } = useDialogContext();
  const { setModified } = useAppContext();
  const { getCleaningPackagesAPI } = Server();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<PackageOption | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState<PackageOption | null>(null);
  const [, forceUpdate] = useState({});
  const [newlyCreatedPackages, setNewlyCreatedPackages] = useState<Set<string>>(new Set());
  // Add state to store original package data
  const [originalPackageData, setOriginalPackageData] = useState<CleaningPackage | null>(null);

  const options = field.options === 'cleaning_packages' ? formData.cleaning_packages : field.options;

  const { data: defaultCleaningPackages = {} } = useQuery({
    queryKey: ['cleaning_packages'],
    queryFn: () => getCleaningPackagesAPI(),
    enabled: field.options === 'cleaning_packages',
  });

  // Build the options array from the cleaning packages or provided options
  const foptions: PackageOption[] = [];
  if (options && typeof options === 'object') {
    Object.keys(options).forEach((key) => {
      foptions.push({
        name: key,
        value: key,
        label: key,
        data: options[key],
      });
    });
  }

  // Get all unique types from the options for filtering
  const allTypes = Array.from(new Set(foptions.map((opt) => opt.data?.type).filter((type): type is string => Boolean(type))));

  // State to store the selected types for filtering
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);

  // Filter options to show only those matching the selected types
  const foptionsToShow =
    selectedTypes.length === 0 ? foptions : foptions.filter((opt) => opt.data?.type !== undefined && selectedTypes.includes(opt.data.type));

  const isDefaultPackage = useCallback(
    (packageName: string) => {
      return !!(defaultCleaningPackages && defaultCleaningPackages[packageName]);
    },
    [defaultCleaningPackages],
  );

  const handleDeleteClick = (item: PackageOption) => {
    setPackageToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (packageToDelete) {
      const currentPackages = formData.cleaning_packages || {};
      const updatedPackages = { ...currentPackages };
      delete updatedPackages[packageToDelete.name];

      const currentCleaningPackage = formData.cleaning_package || {};
      const updatedCleaningPackage = { ...currentCleaningPackage };
      delete updatedCleaningPackage[packageToDelete.name];

      const newFormData = {
        ...formData,
        cleaning_packages: updatedPackages,
        cleaning_package: updatedCleaningPackage,
      };
      setFormData(newFormData);
      setModified(true);

      forceUpdate({});
      setDeleteDialogOpen(false);
      setPackageToDelete(null);
    }
  };

  const isNameUnique = useCallback(
    (name: string, excludeKey?: string) => {
      const currentPackages: Record<string, CleaningPackage> = formData.cleaning_packages || {};

      return !Object.entries(currentPackages).some(([key, packageData]: [string, CleaningPackage]) => {
        // Exclude the package being edited
        if (excludeKey && key === excludeKey) {
          return false;
        }
        return packageData.name === name;
      });
    },
    [formData],
  );

  const generateUniqueName = useCallback(
    (baseName: string, excludeKey?: string) => {
      let counter = 1;
      let uniqueName = baseName;

      while (!isNameUnique(uniqueName, excludeKey)) {
        uniqueName = `${baseName} (${counter})`;
        counter++;
      }

      return uniqueName;
    },
    [isNameUnique],
  );

  const handleDuplicate = useCallback(
    (item: PackageOption) => {
      const currentPackages: Record<string, CleaningPackage> = formData.cleaning_packages || {};

      const getBaseKey = (key: string) => {
        return key.replace(/_copy_\d+$/, '');
      };

      const getBaseName = (name: string) => {
        return name.replace(/\s+Copy\s+\d+$/, '');
      };

      const getNextCopyNumber = (baseKey: string, baseName: string) => {
        let maxNumber = 0;

        Object.keys(currentPackages).forEach((key) => {
          if (key.startsWith(baseKey + '_copy_')) {
            const match = key.match(/_copy_(\d+)$/);
            if (match) {
              const number = parseInt(match[1], 10);
              if (number > maxNumber) {
                maxNumber = number;
              }
            }
          }
        });

        // Check existing names
        Object.values(currentPackages).forEach((packageData: CleaningPackage) => {
          if (packageData.name && packageData.name.startsWith(baseName + ' Copy ')) {
            const match = packageData.name.match(/Copy\s+(\d+)$/);
            if (match) {
              const number = parseInt(match[1], 10);
              if (number > maxNumber) {
                maxNumber = number;
              }
            }
          }
        });

        return maxNumber + 1;
      };

      const baseKey = getBaseKey(item.name);
      const baseName = getBaseName(item.data.name || item.name);

      const nextCopyNumber = getNextCopyNumber(baseKey, baseName);

      const newKey = `${baseKey}_copy_${nextCopyNumber.toString().padStart(2, '0')}`;

      const proposedName = `${baseName} Copy ${nextCopyNumber.toString().padStart(2, '0')}`;
      const newName = generateUniqueName(proposedName);

      // Deep copy of device.parts to avoid reference sharing
      const duplicatedData = {
        ...item.data,
        name: newName,
        imageName: baseKey,
        device: item.data.device
          ? {
              ...item.data.device,
              parts: item.data.device?.parts ? [...item.data.device.parts.map((part: CleaningPackagePart) => ({ ...part }))] : [],
            }
          : null,
      };

      const updatedPackages = {
        ...currentPackages,
        [newKey]: duplicatedData,
      };

      const newFormData = { ...formData, cleaning_packages: updatedPackages };
      setFormData(newFormData);
      setModified(true);

      // Mark this package as newly created
      setNewlyCreatedPackages((prev) => new Set(prev).add(newKey));

      const duplicatedItem: PackageOption = {
        name: newKey,
        value: newKey,
        label: newKey,
        data: updatedPackages[newKey],
      };

      setEditingPackage(duplicatedItem);
      setEditDialogOpen(true);
    },
    [formData, setFormData, setModified, generateUniqueName],
  );

  const handleEdit = (item: PackageOption) => {
    // Save original package data before editing
    const currentPackages = formData.cleaning_packages || {};
    const originalData = currentPackages[item.name] ? JSON.parse(JSON.stringify(currentPackages[item.name])) : null;
    setOriginalPackageData(originalData);

    setEditingPackage(item);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    if (editingPackage) {
      const currentPackages = formData.cleaning_packages || {};
      const oldKey = editingPackage.name;
      const newName = getFormDataItem<string | undefined>(formData, `cleaning_packages.${oldKey}.name`);

      if (newName && newName !== oldKey) {
        // Check uniqueness of the new name
        if (!isNameUnique(newName, oldKey)) {
          // If the name is not unique, show an alert and stay on the form
          openDialog({ text: 'Name conflict' }, <Typography>The name "{newName}" already exists. Please choose a unique name.</Typography>);
          return;
        }

        // The name is unique, proceed normally
        const updatedPackages = { ...currentPackages };
        updatedPackages[oldKey] = {
          ...updatedPackages[oldKey],
          name: newName,
        };

        const newFormData = { ...formData, cleaning_packages: updatedPackages };
        setFormData(newFormData);
        setModified(true);
      } else {
        const updatedPackages = { ...currentPackages };
        const packageData = updatedPackages[oldKey];

        // List of numeric fields to clean
        const numericFields = [
          'efficiency',
          'linear_speed',
          'row_transition_duration',
          'workers',
          'cleaning_opex',
          'capex',
          'brush_max_length',
          'bridge_cost',
          'days_to_clean',
          'max_wind_speed',
          'max_humidity',
          'max_distance',
        ];

        // Clean main numeric fields
        numericFields.forEach((field) => {
          if (packageData[field] !== undefined) {
            const value = packageData[field];
            if (value === '' || value === null || value === undefined || isNaN(value)) {
              packageData[field] = 0;
            }
          }
        });

        // Clean device fields
        if (packageData.device) {
          const deviceNumericFields = ['cost', 'running_lifetime', 'calendar_lifetime', 'yearly_cost'];
          deviceNumericFields.forEach((field) => {
            if (packageData.device[field] !== undefined) {
              const value = packageData.device[field];
              if (value === '' || value === null || value === undefined || isNaN(value)) {
                packageData.device[field] = 0;
              }
            }
          });

          // Clean parts
          if (packageData.device.parts && Array.isArray(packageData.device.parts)) {
            packageData.device.parts = packageData.device.parts.map((part: CleaningPackagePart) => ({
              ...part,
              cost: part.cost === '' || part.cost === null || part.cost === undefined || isNaN(Number(part.cost)) ? 0 : part.cost,
              running_lifetime:
                part.running_lifetime === '' ||
                part.running_lifetime === null ||
                part.running_lifetime === undefined ||
                isNaN(Number(part.running_lifetime))
                  ? 0
                  : part.running_lifetime,
              calendar_lifetime:
                part.calendar_lifetime === '' ||
                part.calendar_lifetime === null ||
                part.calendar_lifetime === undefined ||
                isNaN(Number(part.calendar_lifetime))
                  ? 0
                  : part.calendar_lifetime,
            }));
          }
        }

        // Clean water fields
        if (packageData.water) {
          const waterNumericFields = ['tank_volume', 'consumption', 'refill_duration'];
          waterNumericFields.forEach((field) => {
            if (packageData.water[field] !== undefined) {
              const value = packageData.water[field];
              if (value === '' || value === null || value === undefined || isNaN(value)) {
                packageData.water[field] = 0;
              }
            }
          });
        }

        // Clean fuel fields
        if (packageData.fuel) {
          const fuelNumericFields = ['tank_volume', 'consumption', 'refill_duration'];
          fuelNumericFields.forEach((field) => {
            if (packageData.fuel[field] !== undefined) {
              const value = packageData.fuel[field];
              if (value === '' || value === null || value === undefined || isNaN(value)) {
                packageData.fuel[field] = 0;
              }
            }
          });
        }

        updatedPackages[oldKey] = packageData;
        const newFormData = { ...formData, cleaning_packages: updatedPackages };
        setFormData(newFormData);
        setModified(true);
      }
    }

    setEditDialogOpen(false);
    setEditingPackage(null);

    // Remove the package from the list of newly created packages
    setNewlyCreatedPackages((prev) => {
      const newSet = new Set(prev);
      if (editingPackage) newSet.delete(editingPackage.name);
      return newSet;
    });
  };

  const handleCancelEdit = () => {
    if (editingPackage) {
      const currentPackages = formData.cleaning_packages || {};
      const updatedPackages = { ...currentPackages };

      if (newlyCreatedPackages.has(editingPackage.name)) {
        // Delete newly created package
        delete updatedPackages[editingPackage.name];

        const currentCleaningPackage = formData.cleaning_package || {};
        const updatedCleaningPackage = { ...currentCleaningPackage };
        delete updatedCleaningPackage[editingPackage.name];

        const newFormData = {
          ...formData,
          cleaning_packages: updatedPackages,
          cleaning_package: updatedCleaningPackage,
        };
        setFormData(newFormData);
        setModified(true);
        forceUpdate({});

        // Remove the package from the list of newly created packages
        setNewlyCreatedPackages((prev) => {
          const newSet = new Set(prev);
          newSet.delete(editingPackage.name);
          return newSet;
        });
      } else if (originalPackageData) {
        // Restore original values for existing package
        updatedPackages[editingPackage.name] = originalPackageData;

        const newFormData = {
          ...formData,
          cleaning_packages: updatedPackages,
        };
        setFormData(newFormData);
        setModified(true);
        forceUpdate({});
      }
    }

    setEditDialogOpen(false);
    setEditingPackage(null);
    setOriginalPackageData(null); // Clear original data
  };

  const generateEditFields = (editingPackage: PackageOption) => {
    const packageData: CleaningPackage = editingPackage?.data || {};

    // Function helper to check if a property exists in the original data
    const hasProperty = (obj: object, prop: string) => prop in obj;

    const fields: EditField[] = [
      {
        type: 'group',
        label: 'Informations',
        font_size: 'h5',
        fields: [
          {
            name: `cleaning_packages.${editingPackage.name}.name`,
            label: 'Name',
            type: 'text',
          },
          // Show description if it exists in the original data
          ...(hasProperty(packageData, 'description')
            ? [
                {
                  name: `cleaning_packages.${editingPackage.name}.description`,
                  label: 'Description',
                  type: 'text',
                  multiline: true,
                  rows: 4,
                  size: 6,
                },
              ]
            : []),
          // Show note if it exists in the original data
          ...(hasProperty(packageData, 'note')
            ? [
                {
                  name: `cleaning_packages.${editingPackage.name}.note`,
                  label: 'Note',
                  type: 'text',
                  multiline: true,
                  rows: 4,
                  size: 6,
                },
              ]
            : []),
        ],
      },
    ];

    // Add Cleaning Package group only if at least one property exists
    const cleaningPackageProps = [
      'efficiency',
      'linear_speed',
      'row_transition_duration',
      'workers',
      'cleaning_opex',
      'capex',
      'brush_max_length',
      'bridge_cost',
      'days_to_clean',
    ];
    const hasCleaningPackageData = cleaningPackageProps.some((prop) => hasProperty(packageData, prop));

    if (hasCleaningPackageData) {
      const cleaningPackageFields: EditField[] = [];

      // Show efficiency if it exists in the original data
      if (hasProperty(packageData, 'efficiency')) {
        cleaningPackageFields.push({
          name: `cleaning_packages.${editingPackage.name}.efficiency`,
          label: 'Efficiency',
          type: 'number',
          size: 6,
        });
      }
      if (hasProperty(packageData, 'linear_speed')) {
        cleaningPackageFields.push({
          name: `cleaning_packages.${editingPackage.name}.linear_speed`,
          label: 'Linear Speed (m/h)',
          type: 'number',
          size: 6,
        });
      }
      if (hasProperty(packageData, 'row_transition_duration')) {
        cleaningPackageFields.push({
          name: `cleaning_packages.${editingPackage.name}.row_transition_duration`,
          label: 'Row Transition Duration (min)',
          type: 'number',
          size: 6,
        });
      }
      if (hasProperty(packageData, 'workers')) {
        cleaningPackageFields.push({
          name: `cleaning_packages.${editingPackage.name}.workers`,
          label: 'Number of Workers',
          type: 'number',
          size: 6,
        });
      }
      if (hasProperty(packageData, 'cleaning_opex')) {
        cleaningPackageFields.push({
          name: `cleaning_packages.${editingPackage.name}.cleaning_opex`,
          label: 'Opex cost per cleaning ($)',
          type: 'number',
          size: 6,
        });
      }
      if (hasProperty(packageData, 'capex')) {
        cleaningPackageFields.push({
          name: `cleaning_packages.${editingPackage.name}.capex`,
          label: 'Capex cost ($)',
          type: 'number',
          size: 6,
        });
      }
      if (hasProperty(packageData, 'brush_max_length')) {
        cleaningPackageFields.push({
          name: `cleaning_packages.${editingPackage.name}.brush_max_length`,
          label: 'Brush Max Length (m)',
          type: 'number',
          size: 6,
        });
      }
      if (hasProperty(packageData, 'bridge_cost')) {
        cleaningPackageFields.push({
          name: `cleaning_packages.${editingPackage.name}.bridge_cost`,
          label: 'Bridge Cost ($)',
          type: 'number',
          size: 6,
        });
      }
      if (hasProperty(packageData, 'days_to_clean')) {
        cleaningPackageFields.push({
          name: `cleaning_packages.${editingPackage.name}.days_to_clean`,
          label: 'Time to clean the plant (days)',
          type: 'number',
          size: 6,
        });
      }

      fields.push({
        type: 'group',
        label: 'Cleaning Package',
        font_size: 'h5',
        fields: cleaningPackageFields,
      });
    }

    // Add Device group only if device exists
    if (packageData.device) {
      const deviceFields: EditField[] = [];

      if (hasProperty(packageData.device, 'name')) {
        deviceFields.push({
          name: `cleaning_packages.${editingPackage.name}.device.name`,
          label: 'Device Name',
          type: 'text',
          size: 6,
        });
      }
      if (hasProperty(packageData.device, 'cost')) {
        deviceFields.push({
          name: `cleaning_packages.${editingPackage.name}.device.cost`,
          label: 'Device Cost ($)',
          type: 'number',
          size: 6,
        });
      }
      if (hasProperty(packageData.device, 'running_lifetime')) {
        deviceFields.push({
          name: `cleaning_packages.${editingPackage.name}.device.running_lifetime`,
          label: 'Device Running Lifetime (h)',
          type: 'number',
          size: 6,
        });
      }
      if (hasProperty(packageData.device, 'calendar_lifetime')) {
        deviceFields.push({
          name: `cleaning_packages.${editingPackage.name}.device.calendar_lifetime`,
          label: 'Device Lifetime (years)',
          type: 'number',
          size: 6,
        });
      }
      if (hasProperty(packageData.device, 'yearly_cost')) {
        deviceFields.push({
          name: `cleaning_packages.${editingPackage.name}.device.yearly_cost`,
          label: 'Device Yearly Cost ($)',
          type: 'number',
          size: 6,
        });
      }

      // Add parts only if they exist in the original data
      if (hasProperty(packageData.device, 'parts')) {
        deviceFields.push({
          type: 'parts',
          name: `cleaning_packages.${editingPackage.name}.device.parts`,
          label: 'Device Parts',
          size: 12,
        });
      }

      if (deviceFields.length > 0) {
        fields.push({
          type: 'group',
          label: 'Device',
          font_size: 'h5',
          fields: deviceFields,
        });
      }
    }

    // Add Fuel group only if fuel exists
    if (packageData.fuel) {
      const fuelProps = ['tank_volume', 'consumption', 'refill_duration'];
      const hasFuelData = fuelProps.some((prop) => hasProperty(packageData.fuel!, prop));

      if (hasFuelData) {
        const fuelFields: EditField[] = [];

        if (hasProperty(packageData.fuel, 'tank_volume')) {
          fuelFields.push({
            name: `cleaning_packages.${editingPackage.name}.fuel.tank_volume`,
            label: 'Fuel Tank Volume (L)',
            type: 'number',
            size: 6,
          });
        }
        if (hasProperty(packageData.fuel, 'consumption')) {
          fuelFields.push({
            name: `cleaning_packages.${editingPackage.name}.fuel.consumption`,
            label: 'Fuel Consumption (L/h)',
            type: 'number',
            size: 6,
          });
        }
        if (hasProperty(packageData.fuel, 'refill_duration')) {
          fuelFields.push({
            name: `cleaning_packages.${editingPackage.name}.fuel.refill_duration`,
            label: 'Fuel Refill Duration (min)',
            type: 'number',
            size: 6,
          });
        }

        fields.push({
          type: 'group',
          label: 'Fuel',
          font_size: 'h5',
          fields: fuelFields,
        });
      }
    }

    // Add Water group only if water exists
    if (packageData.water) {
      const waterProps = ['tank_volume', 'consumption', 'refill_duration'];
      const hasWaterData = waterProps.some((prop) => hasProperty(packageData.water!, prop));

      if (hasWaterData) {
        const waterFields: EditField[] = [];

        if (hasProperty(packageData.water, 'tank_volume')) {
          waterFields.push({
            name: `cleaning_packages.${editingPackage.name}.water.tank_volume`,
            label: 'Water Tank Volume (m³)',
            type: 'number',
            size: 6,
          });
        }
        if (hasProperty(packageData.water, 'consumption')) {
          waterFields.push({
            name: `cleaning_packages.${editingPackage.name}.water.consumption`,
            label: 'Water Consumption (m³/h)',
            type: 'number',
            size: 6,
          });
        }
        if (hasProperty(packageData.water, 'refill_duration')) {
          waterFields.push({
            name: `cleaning_packages.${editingPackage.name}.water.refill_duration`,
            label: 'Water Refill Duration (min)',
            type: 'number',
            size: 6,
          });
        }

        fields.push({
          type: 'group',
          label: 'Water',
          font_size: 'h5',
          fields: waterFields,
        });
      }
    }

    // Add Weather group only if at least one property exists
    const weatherProps = ['max_wind_speed', 'max_humidity', 'max_distance'];
    const hasWeatherData = weatherProps.some((prop) => hasProperty(packageData, prop));

    if (hasWeatherData) {
      const weatherFields: EditField[] = [];

      if (hasProperty(packageData, 'max_wind_speed')) {
        weatherFields.push({
          name: `cleaning_packages.${editingPackage.name}.max_wind_speed`,
          label: 'Max Wind Speed',
          type: 'number',
          size: 6,
        });
      }
      if (hasProperty(packageData, 'max_humidity')) {
        weatherFields.push({
          name: `cleaning_packages.${editingPackage.name}.max_humidity`,
          label: 'Max Humidity',
          type: 'number',
          size: 6,
        });
      }
      if (hasProperty(packageData, 'max_distance')) {
        weatherFields.push({
          name: `cleaning_packages.${editingPackage.name}.max_distance`,
          label: 'Max Distance',
          type: 'number',
          size: 6,
        });
      }

      fields.push({
        type: 'group',
        label: 'Weather',
        font_size: 'h5',
        fields: weatherFields,
      });
    }

    return fields;
  };

  const handleAddPart = () => {
    const currentParts = getFormDataItem<CleaningPackagePart[]>(formData, `cleaning_packages.${editingPackage!.name}.device.parts`) || [];
    const newPart = {
      name: '',
      cost: 0,
      running_lifetime: 0,
      calendar_lifetime: 0,
    };
    const updatedParts = [...currentParts, newPart];

    // Update directly in formData like other functions
    const currentPackages = formData.cleaning_packages || {};
    const updatedPackages = { ...currentPackages };
    if (!updatedPackages[editingPackage!.name].device) {
      updatedPackages[editingPackage!.name].device = {};
    }
    updatedPackages[editingPackage!.name].device.parts = updatedParts;

    const newFormData = { ...formData, cleaning_packages: updatedPackages };
    setFormData(newFormData);
    setModified(true);
    forceUpdate({});
  };

  const handleRemovePart = (index: number) => {
    const currentParts = getFormDataItem<CleaningPackagePart[]>(formData, `cleaning_packages.${editingPackage!.name}.device.parts`) || [];
    const updatedParts = currentParts.filter((_, i: number) => i !== index);

    // Update directly in formData
    const currentPackages = formData.cleaning_packages || {};
    const updatedPackages = { ...currentPackages };
    if (!updatedPackages[editingPackage!.name].device) {
      updatedPackages[editingPackage!.name].device = {};
    }
    updatedPackages[editingPackage!.name].device.parts = updatedParts;

    const newFormData = { ...formData, cleaning_packages: updatedPackages };
    setFormData(newFormData);
    setModified(true);
    forceUpdate({});
  };

  const handlePartChange = (index: number, field: string, value: string | number) => {
    const currentParts = getFormDataItem<CleaningPackagePart[]>(formData, `cleaning_packages.${editingPackage!.name}.device.parts`) || [];
    const updatedParts = [...currentParts];

    // For numeric fields, use 0 if the value is empty
    let finalValue = value;
    if (field === 'cost' || field === 'running_lifetime' || field === 'calendar_lifetime') {
      finalValue = value === '' || value === null || value === undefined || isNaN(Number(value)) ? 0 : value;
    }

    updatedParts[index] = { ...updatedParts[index], [field]: finalValue };

    // Update directly in formData
    const currentPackages = formData.cleaning_packages || {};
    const updatedPackages = { ...currentPackages };
    if (!updatedPackages[editingPackage!.name].device) {
      updatedPackages[editingPackage!.name].device = {};
    }
    updatedPackages[editingPackage!.name].device.parts = updatedParts;

    const newFormData = { ...formData, cleaning_packages: updatedPackages };
    setFormData(newFormData);
    setModified(true);
    forceUpdate({});
  };

  // Add local state to control value display
  const [partInputValues, setPartInputValues] = useState<{ [key: string]: string }>({});

  const renderGroupField = (field: EditField) => {
    let isHidden = hasOwn(field, 'isHidden') ? getFormDataItem<boolean>(formData, field.isHidden!) : false;

    if (hasOwn(field, 'revertHidden') && field?.revertHidden) {
      isHidden = !isHidden;
    }

    if (isHidden) {
      return null;
    }

    return (
      <Grid
        size={12}
        key={field.label}
        sx={{ padding: 1, border: '1px solid rgba(0, 0, 0, 0.12)', borderRadius: '4px', boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.2)' }}
      >
        <Box display="flex" sx={{ mb: 1 }}>
          <Typography color="text.primary" variant={field.font_size || 'h6'}>
            {field.label}
          </Typography>
          {hasOwn(field, 'help') ? <ChartHelpTooltip chartType={field.help!} /> : null}
        </Box>
        <Grid container spacing={2}>
          {field.fields!.map((subField) => renderEditField(subField))}
        </Grid>
      </Grid>
    );
  };

  const renderEditField = (field: EditField) => {
    let isHidden = hasOwn(field, 'isHidden') ? getFormDataItem<boolean>(formData, field.isHidden!) : false;

    if (hasOwn(field, 'revertHidden') && field?.revertHidden) {
      isHidden = !isHidden;
    }

    if (isHidden) {
      return null;
    }

    switch (field.type) {
      case 'text':
        return <TextField field={field} />;
      case 'number':
        return <NumberField field={field} />;
      case 'select':
        return <SelectField field={field} />;
      case 'checkbox':
        return <CheckBoxField field={field} />;
      case 'group':
        return renderGroupField(field);
      case 'parts':
        return renderPartsField(field);
      default:
        return null;
    }
  };

  // Add this new function to render parts
  const renderPartsField = (field: EditField) => {
    const deviceParts = getFormDataItem<CleaningPackagePart[]>(formData, field.name!) || [];

    // Only show the parts field if it exists in the original data
    if (!hasOwn(editingPackage?.data.device, 'parts')) {
      return null;
    }

    return (
      <Grid size={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">{field.label}</Typography>
          <Button startIcon={<AddIcon />} onClick={handleAddPart} variant="outlined" size="small">
            Add Part
          </Button>
        </Box>

        {deviceParts.map((part: CleaningPackagePart, index: number) => (
          <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">Part {index + 1}</Typography>
              <IconButton size="small" onClick={() => handleRemovePart(index)} color="error">
                <RemoveIcon />
              </IconButton>
            </Box>
            <Grid container spacing={2}>
              <Grid size={6}>
                <MuiTextField
                  label="Part Name"
                  fullWidth
                  size="small"
                  value={part.name || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePartChange(index, 'name', e.target.value)}
                />
              </Grid>
              <Grid size={3}>
                <MuiTextField
                  label="Cost ($)"
                  type="text"
                  fullWidth
                  size="small"
                  value={partInputValues[`cost-${index}`] !== undefined ? partInputValues[`cost-${index}`] : part.cost || 0}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value;
                    if (isValidNumberInput(value)) {
                      // Update display
                      setPartInputValues((prev) => ({ ...prev, [`cost-${index}`]: value }));
                      handlePartChange(index, 'cost', parseFloat(value) || 0);
                    }
                  }}
                />
              </Grid>
              <Grid size={3}>
                <MuiTextField
                  label="Running Lifetime (h)"
                  type="text"
                  fullWidth
                  size="small"
                  value={
                    partInputValues[`running_lifetime-${index}`] !== undefined
                      ? partInputValues[`running_lifetime-${index}`]
                      : part.running_lifetime || 0
                  }
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value;
                    if (isValidNumberInput(value)) {
                      // Update display
                      setPartInputValues((prev) => ({ ...prev, [`running_lifetime-${index}`]: value }));
                      handlePartChange(index, 'running_lifetime', parseFloat(value) || 0);
                    }
                  }}
                />
              </Grid>
              <Grid size={3}>
                <MuiTextField
                  label="Calendar Lifetime (years)"
                  type="text"
                  fullWidth
                  size="small"
                  value={
                    partInputValues[`calendar_lifetime-${index}`] !== undefined
                      ? partInputValues[`calendar_lifetime-${index}`]
                      : part.calendar_lifetime || 0
                  }
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value;
                    if (isValidNumberInput(value)) {
                      // Update display
                      setPartInputValues((prev) => ({ ...prev, [`calendar_lifetime-${index}`]: value }));
                      handlePartChange(index, 'calendar_lifetime', parseFloat(value) || 0);
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        ))}

        {hasOwn(editingPackage?.data.device, 'parts') && deviceParts.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            No parts added yet. Click "Add Part" to add device parts.
          </Typography>
        )}
      </Grid>
    );
  };

  const renderEditDialog = () => {
    if (!editingPackage) return null;

    const editFields = generateEditFields(editingPackage);

    return (
      <Dialog open={editDialogOpen} onClose={handleCancelEdit} maxWidth="md" fullWidth>
        <DialogTitle>
          Edit Cleaning Package: {editingPackage?.data?.name} | Type: {getTypeLabel(editingPackage?.data?.type)}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {editFields.map((field) => renderEditField(field))}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderSpecialMultiSelectItem = (field_name: string | undefined, item: PackageOption) => (
    <Grid key={item.name} size={{ xs: 12, md: 4, xl: 3 }}>
      <Card>
        <CardMedia sx={{ height: 150 }} image={'/' + (item.data.imageName || item.value) + '.png'} title={item.label}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1 }}>
            <IconButton
              aria-label="info"
              size="small"
              onClick={() => {
                const content = (
                  <>
                    <Grid container>
                      <Grid size={6} sx={{ mt: 2 }}>
                        <Typography sx={{ mb: 1 }}>{item.data.description}</Typography>
                        <Typography variant="caption">{item.data.note}</Typography>
                      </Grid>
                      <Grid size={6}>
                        <ul>
                          <li>
                            <b>Efficiency:</b> {Number(item.data.efficiency) * 100}%
                          </li>
                          {item.data.linear_speed && (
                            <li>
                              <b>Linear Speed:</b> {item.data.linear_speed} (m/h)
                            </li>
                          )}
                          {item.data.days_to_clean && (
                            <li>
                              <b>Days to clean:</b> {item.data.days_to_clean} days
                            </li>
                          )}
                          <li>
                            <b>Type:</b> {getTypeLabel(item.data.type)}
                          </li>
                          {item.data.device?.cost && item.data.device?.name && (
                            <li>
                              <b>Cost {item.data.device?.name}:</b> {item.data.device?.cost} ($)
                            </li>
                          )}
                          {item.data.device?.parts?.map((part: CleaningPackagePart, index: number) => (
                            <li key={index}>
                              <b>Cost {part.name}:</b> {part.cost} ($)
                            </li>
                          ))}
                          <li>
                            <b>Water consumption:</b> {item.data.water?.consumption} (m³/h)
                          </li>
                          <li>
                            <b>Fuel consumption:</b> {item.data.fuel?.consumption} (L/h)
                          </li>
                        </ul>
                      </Grid>
                    </Grid>
                  </>
                );
                openDialog({ text: item.data.name! }, content);
              }}
            >
              <InfoIcon fontSize="inherit" />
            </IconButton>
          </Box>
        </CardMedia>
        <CardContent sx={{ pb: 0 }}>
          <Typography gutterBottom variant="h5" component="div">
            {item.data.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            Efficiency: {Number(item.data.efficiency) * 100}%
          </Typography>
          {item.data.linear_speed && (
            <Typography variant="caption" color="text.secondary" component="div">
              Linear speed: {item.data.linear_speed} m/h
            </Typography>
          )}
          {item.data.days_to_clean && (
            <Typography variant="caption" color="text.secondary" component="div">
              Days to clean: {item.data.days_to_clean} days
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" component="div">
            Type: {getTypeLabel(item.data.type)}
          </Typography>
        </CardContent>
        <CardActions sx={{ pt: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Checkbox
              name={item.name}
              checked={!!getFormDataItem(formData, field_name + '.' + item.name)}
              onChange={(e) => {
                const isChecked = e.target.checked;

                if (isChecked) {
                  // If checked, add with the value true
                  handleChange(
                    { target: { name: field_name + '.' + item.name, value: item.name, type: e.target.type, checked: true } },
                    item,
                  );
                } else {
                  // If unchecked, completely remove the property
                  const currentCleaningPackage = formData.cleaning_package || {};
                  const updatedCleaningPackage = { ...currentCleaningPackage };
                  delete updatedCleaningPackage[item.name];

                  const newFormData = { ...formData, cleaning_package: updatedCleaningPackage };
                  setFormData(newFormData);
                  setModified(true);
                  forceUpdate({});
                }
              }}
              inputProps={{ 'aria-label': 'controlled' }}
            />
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <IconButton
                aria-label="edit"
                size="small"
                onClick={() => handleEdit(item)}
                disabled={isDefaultPackage(item.name)}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                  opacity: isDefaultPackage(item.name) ? 0.5 : 1,
                }}
              >
                <EditIcon fontSize="small" color={isDefaultPackage(item.name) ? 'disabled' : 'primary'} />
              </IconButton>
              <IconButton
                aria-label="duplicate"
                size="small"
                onClick={() => handleDuplicate(item)}
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.8)' }}
                color="primary"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
              <IconButton
                aria-label="delete"
                size="small"
                onClick={() => handleDeleteClick(item)}
                disabled={isDefaultPackage(item.name)}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                  opacity: isDefaultPackage(item.name) ? 0.5 : 1,
                }}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </CardActions>
      </Card>
    </Grid>
  );

  return (
    <>
      {/* Filtering header with a MultiSelect for types */}
      <Box sx={{ mt: 2, width: '30%' }}>
        <Autocomplete
          multiple
          options={allTypes}
          value={selectedTypes}
          onChange={(_, value) => setSelectedTypes(value)}
          renderTags={(value: string[], getTagProps) =>
            value.map((option: string, index: number) => <Chip variant="outlined" label={option} {...getTagProps({ index })} />)
          }
          renderInput={(params) => <MuiTextField {...params} variant="outlined" label="Filter by type" placeholder="Type" />}
          size="small"
          disableCloseOnSelect
        />
      </Box>
      {/* Render the filtered options list */}
      <Grid container spacing={2} size={field.size || 12} key={field.name} sx={{ mt: 2 }}>
        {foptionsToShow.map((item) => renderSpecialMultiSelectItem(field.name, item))}
      </Grid>
      {renderEditDialog()}

      {/* Dialog de confirmation de suppression utilisant le composant Dialog personnalisé */}
      <CustomDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title={{ text: 'Supprimer le package de nettoyage' }}
        size="sm"
      >
        <Typography sx={{ mb: 3 }}>
          Are you sure you want to delete the package "{packageToDelete?.data?.name}"? This action cannot be undone.
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </Stack>
      </CustomDialog>
    </>
  );
};

export default SpecialMultiSelect;
